import { FeatureFlagName } from 'config/featureFlags';
import FeatureFlagManager from 'config/featureFlags/FeatureFlagManager';
import { PixSampleTypes } from 'constants/assessment_types';
import { UserRole, Roles } from 'constants/roles';
import {
  AnyRoute,
  AllRoutes,
  PatientRoute,
  PatientRoutes,
  ProviderRoute,
  ProviderRoutes,
  ExecutiveRoute,
  ExecutiveRoutes,
  SuperAdminRoute,
  SuperAdminRoutes,
  AdminRoute,
  AdminRoutes,
  MenuItem,
} from 'constants/routes';
import { TFunction } from 'i18next';
import { appTranslationKeys } from '../../../i18n';

const PATIENT_NAV = [PatientRoutes.DASHBOARD, PatientRoutes.RESOURCES, PatientRoutes.HELP] as PatientRoute[];

const PROVIDER_NAV = [
  ProviderRoutes.DASHBOARD,
  ...(FeatureFlagManager.get<boolean>(FeatureFlagName.isGroupTherapyEnabled) ? [AllRoutes.GROUP_THERAPY_BASE] : []),
  ProviderRoutes.PRINTABLE_ASSESSMENTS,
  ProviderRoutes.HELP,
] as ProviderRoute[];

const FACILITY_DIRECTOR_NAV = [
  ExecutiveRoutes.DASHBOARD,
  ExecutiveRoutes.FACILITY_USER_ACCOUNTS,
  ...(FeatureFlagManager.get<boolean>(FeatureFlagName.isGroupTherapyEnabled) ? [AllRoutes.GROUP_THERAPY_BASE] : []),
  ExecutiveRoutes.REPORTS,
  ExecutiveRoutes.PRINTABLE_ASSESSMENTS,
  ExecutiveRoutes.HELP,
  ExecutiveRoutes.FACILITY_LIBRARY,
] as ExecutiveRoute[];

const EXECUTIVE_ADMIN_NAV = [
  ExecutiveRoutes.REPORTS,
  ExecutiveRoutes.FACILITY_USER_ACCOUNTS,
  ExecutiveRoutes.PRINTABLE_ASSESSMENTS,
  ExecutiveRoutes.HELP,
  ExecutiveRoutes.FACILITY_LIBRARY,
] as ExecutiveRoute[];

const ADMIN_NAV = [AdminRoutes.DASHBOARD, AdminRoutes.FACILITY_USER_ACCOUNTS, AdminRoutes.ADMIN_REPORTS, AdminRoutes.FACILITY_LIBRARY] as AdminRoute[];

const SUPER_ADMIN_PRIMARY_NAV = [
  SuperAdminRoutes.DASHBOARD,
  SuperAdminRoutes.FACILITY_USER_ACCOUNTS,
  SuperAdminRoutes.ADMIN_REPORTS,
  SuperAdminRoutes.ASSESSMENT_EDITOR,
  SuperAdminRoutes.FACILITY_LIBRARY,
] as SuperAdminRoute[];

export const ADMIN_REFERENCE_NAV = [SuperAdminRoutes.HELP, SuperAdminRoutes.RESOURCES] as SuperAdminRoute[];

export const hasPixAccess = (pixSampleType: string | null | undefined): boolean => {
  if (!pixSampleType || pixSampleType === '') return false;

  const pixSampleTypeInt = parseInt(pixSampleType, 10);
  return pixSampleTypeInt > 0 && pixSampleTypeInt !== PixSampleTypes.NONE;
};

const getUHSNavText = (route: AnyRoute, t: TFunction): string => {
  switch (route) {
    default:
      return 'Route Not Found';
    case AllRoutes.ACCOUNT:
      return 'Account';
    case AllRoutes.ASSESSMENT_EDITOR:
      return 'Assessment Editor';
    case AllRoutes.DASHBOARD:
      return t(appTranslationKeys.nav.home);
    case AllRoutes.HELP:
      return t(appTranslationKeys.nav.help);
    case AllRoutes.FACILITY_LIBRARY:
      return 'Facility Library';
    case AllRoutes.PATIENT_ACCOUNTS:
      return 'Patient Accounts';
    case AllRoutes.GROUP_THERAPY_BASE:
      return 'Group Therapy Sessions';
    case AllRoutes.PRINTABLE_ASSESSMENTS:
      return 'Printable Assessments';
    case AllRoutes.REPORTS:
    case AdminRoutes.ADMIN_REPORTS:
      return 'Reports';
    case AllRoutes.RESOURCES:
      return t(appTranslationKeys.nav.resources);
    case AllRoutes.FACILITY_USER_ACCOUNTS:
      return 'Facility User Accounts';
    case AllRoutes.CORE_MEASURES_WORKLIST:
      return 'Worklist';
    case AllRoutes.CORE_MEASURES_FACILITY_INFO:
      return 'Facility Info';
    case AllRoutes.PIX_DATA_ENTRY:
      return 'Downtime Only PIX Entry';
  }
};

const getDefaultNavText = (route: AnyRoute, t: TFunction): string => {
  switch (route) {
    default:
      return 'Route Not Found';
    case AllRoutes.ACCOUNT:
      return 'Account';
    case AllRoutes.ASSESSMENT_EDITOR:
      return 'Assessment Editor';
    case AllRoutes.DASHBOARD:
      return t(appTranslationKeys.nav.home);
    case AllRoutes.HELP:
      return t(appTranslationKeys.nav.help);
    case AllRoutes.FACILITY_LIBRARY:
      return 'Facility Library';
    case AllRoutes.PATIENT_ACCOUNTS:
      return 'Patient Accounts';
    case AllRoutes.GROUP_THERAPY_BASE:
      return 'Group Therapy Sessions';
    case AllRoutes.PRINTABLE_ASSESSMENTS:
      return 'Printable Assessments';
    case AllRoutes.REPORTS:
    case AdminRoutes.ADMIN_REPORTS:
      return 'Reports';
    case AllRoutes.RESOURCES:
      return t(appTranslationKeys.nav.resources);
    case AllRoutes.FACILITY_USER_ACCOUNTS:
      return 'Facility User Accounts';
    case AllRoutes.CORE_MEASURES_WORKLIST:
      return 'Worklist';
    case AllRoutes.CORE_MEASURES_FACILITY_INFO:
      return 'Facility Info';
    case AllRoutes.PIX_DATA_ENTRY:
      return 'PIX Data Entry';
  }
};

const coreMeasuresSubmenu: AnyRoute[] = [AllRoutes.CORE_MEASURES_WORKLIST, AllRoutes.CORE_MEASURES_FACILITY_INFO];

export const GetNavText = (route: AnyRoute, isUHS: boolean, t: TFunction): string => {
  if (isUHS) {
    return getUHSNavText(route, t);
  }
  return getDefaultNavText(route, t);
};

export const convertToMenuItems = (routes: AnyRoute[]): MenuItem[] => {
  const menuItems: MenuItem[] = [];
  for (let i = 0; i < routes.length; i += 1) {
    menuItems.push({ childItems: [], menuTitle: '', menuLink: routes[i] });
  }
  return menuItems;
};

export const GetNavOptions = (
  role: UserRole | null | undefined,
  accountID: string | null | undefined,
  hasCoreMeasuresAccess: boolean,
  hasOutcomesAccess: boolean,
  pixSampleType: string | undefined,
): MenuItem[] => {
  let menuItems: MenuItem[] = [];

  switch (role) {
    default:
      break;
    case Roles.PATIENT:
      // Array.from() is used to create a shallow copy of the array
      menuItems = convertToMenuItems(PATIENT_NAV);
      break;
    case Roles.PROVIDER:
      menuItems = convertToMenuItems(PROVIDER_NAV);
      break;
    case Roles.FACILITY_DIRECTOR:
      menuItems = convertToMenuItems(FACILITY_DIRECTOR_NAV);
      break;
    case Roles.FACILITY_EXECUTIVE:
    case Roles.CORPORATION:
    case Roles.SUPER_CORPORATION:
      menuItems = convertToMenuItems(EXECUTIVE_ADMIN_NAV);
      break;
    case Roles.ADMIN:
      menuItems = convertToMenuItems(ADMIN_NAV);
      break;
    case Roles.SUPER_ADMIN:
      menuItems = convertToMenuItems(SUPER_ADMIN_PRIMARY_NAV);
      break;
  }

  // If a Core Measures facility is selected, need to include the CM Links
  if (FeatureFlagManager.get<boolean>(FeatureFlagName.isCoreMeasuresEnabled)) {
    const userCMLinks: AnyRoute[] = [];
    if (hasCoreMeasuresAccess) {
      userCMLinks.push(...Array.from(coreMeasuresSubmenu));
    }
    if (hasPixAccess(pixSampleType) && role !== Roles.SUPER_CORPORATION && role !== Roles.CORPORATION) {
      userCMLinks.push(AllRoutes.PIX_DATA_ENTRY);
    }
    if (userCMLinks.length > 0) {
      menuItems.splice(1, 0, { childItems: userCMLinks, menuTitle: 'Core Measures', menuLink: null });
    }
  }

  // If user has outcomes access, add Patients Accounts link
  if (hasOutcomesAccess && role !== Roles.PATIENT) {
    menuItems.splice(1, 0, { childItems: [], menuTitle: '', menuLink: AllRoutes.PATIENT_ACCOUNTS });
  } else if (role !== Roles.PATIENT) {
    // If no outcomes access, remove Home link
    const homeIndex = menuItems.findIndex((x) => x.menuLink === AllRoutes.DASHBOARD);
    if (homeIndex >= 0) {
      menuItems.splice(homeIndex, 1);
    }
    // Remove printable assessments and group therapy routes
    const printAssessmentsIndex = menuItems.findIndex((x) => x.menuLink === AllRoutes.PRINTABLE_ASSESSMENTS);
    if (printAssessmentsIndex >= 0) {
      menuItems.splice(printAssessmentsIndex, 1);
    }
    const groupTherapyIndex = menuItems.findIndex((x) => x.menuLink === AllRoutes.GROUP_THERAPY_BASE);
    if (groupTherapyIndex >= 0) {
      menuItems.splice(groupTherapyIndex, 1);
    }
  }

  if (role === Roles.SUPER_ADMIN || role === Roles.ADMIN) {
    menuItems.push({ childItems: ADMIN_REFERENCE_NAV, menuTitle: 'References', menuLink: null });
  }
  return menuItems;
};
